import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircleIcon as CheckCircle, CircleIcon as Circle, ArrowRightIcon as ArrowRight, SparkleIcon as Sparkle } from "@phosphor-icons/react";

/**
 * "Complete your kennel" benefits checklist — appears on the kennel
 * detail page for owners only.
 *
 * Each item is a benefit the owner unlocks by completing the step,
 * not just a task to tick off. Once every item is done the card
 * collapses into a celebratory "All set" footer so it stays visible
 * (for re-engagement) but never nags.
 *
 * Items pulled live from the breeder doc:
 *   • Photo uploaded         → photo_url
 *   • Bio written            → bio
 *   • Custom URL claimed     → slug
 *   • Dogs registered        → /dogs?breeder_id=X count
 *   • Verified seal earned   → verified
 *
 * The verified-seal item links into the 7-step verification drawer
 * (`/breeders?verify=<id>` for admins, the verification progress
 * badge already on the header for owners).
 */
export default function KennelBenefitsChecklist({ breeder, dogCount }) {
  const [verification, setVerification] = useState(null);

  useEffect(() => {
    if (!breeder?.id) return;
    api.get(`/public/kennel-verification/${breeder.id}`)
      .then((r) => setVerification(r.data))
      .catch(() => setVerification(null));
  }, [breeder?.id]);

  if (!breeder) return null;

  const stepsPassed = verification?.steps
    ? Object.values(verification.steps).filter((s) => s?.status === "passed").length
    : null;
  const stepsTotal = verification?.steps ? Object.keys(verification.steps).length : 7;

  const items = [
    {
      key: "photo",
      done: !!(breeder.photo_url || breeder.logo_url),
      title: "Upload a kennel photo",
      benefit: "Cards & share images come alive — buyers remember faces, not initials.",
      cta: null, // inline button on header
    },
    {
      key: "bio",
      done: !!(breeder.bio && breeder.bio.trim().length >= 40),
      title: "Write a short bio (40+ chars)",
      benefit: "Your bio appears on /k/{slug} and in share previews. It's your kennel's voice.",
      cta: null,
    },
    {
      key: "slug",
      done: !!breeder.slug,
      title: "Claim your custom URL",
      benefit: "A memorable /k/your-name URL beats /breeders/<uuid> every time — and ranks better.",
      cta: null,
    },
    {
      key: "dogs",
      done: (dogCount || 0) > 0,
      title: "Link at least one dog",
      benefit: "Your dogs power the public profile and unlock the Marketplace + Breeding Matchmaker.",
      cta: { to: "/dogs/new", label: "Add a dog" },
    },
    {
      key: "verified",
      done: !!breeder.verified,
      title: stepsPassed != null
        ? `Earn the verified seal (${stepsPassed}/${stepsTotal} steps passed)`
        : "Earn the verified seal",
      benefit: "The WaxSeal medallion appears next to your kennel name everywhere on the platform.",
      cta: null,
    },
  ];

  const doneCount = items.filter((i) => i.done).length;
  const allDone = doneCount === items.length;

  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl p-5 md:p-6 mb-6 shadow-luxury-light"
      data-testid="kennel-benefits-checklist"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={14} weight="fill" className="text-gold" />
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
          {allDone ? "All set" : `Get the most from your kennel · ${doneCount}/${items.length}`}
        </p>
      </div>
      <h2 className="font-spectral text-2xl tracking-tight">
        {allDone
          ? "Your kennel is fully set up"
          : "Complete your kennel page"}
      </h2>
      <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl leading-relaxed">
        {allDone
          ? "Every section is filled in — your public page is working hard for you across the registry."
          : "Each step unlocks more reach, more trust, and more buyer signal across the platform."}
      </p>
      <div className="gold-hairline mt-4 mb-4 max-w-md" />

      <ul className="space-y-3">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex items-start gap-3"
            data-testid={`kennel-checklist-${item.key}`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {item.done ? (
                <CheckCircle size={20} weight="fill" className="text-[#0F8043]" />
              ) : (
                <Circle size={20} weight="duotone" className="text-[#C4C4C4]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold leading-snug ${item.done ? "text-[#6B6B6B] line-through" : "text-ink"}`}>
                {item.title}
              </p>
              {!item.done && (
                <p className="text-[12.5px] text-[#6B6B6B] mt-0.5 leading-relaxed">
                  {item.benefit}
                </p>
              )}
              {!item.done && item.cta && (
                <Link
                  to={item.cta.to}
                  className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-semibold text-crimson hover:underline"
                  data-testid={`kennel-checklist-cta-${item.key}`}
                >
                  {item.cta.label}
                  <ArrowRight size={11} weight="bold" />
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
